import { useEffect, useState } from "react";
import { settingsApi, settingsSaveErrorMessage } from "./api";
import { SkeletonGrid } from "./Skeleton";
import type { EditableSettings } from "./settings-form";
import type { AccessSnapshot, AdminRole, SettingsSnapshot, TitleDeletionAccess } from "./types";

function shanghaiResetLabel(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

interface TitleSettingsProps {
  values: EditableSettings;
  access: TitleDeletionAccess | null;
  deletionDisabled: boolean;
  onChange: (patch: Partial<EditableSettings>) => void;
  onDeleted: (snapshot: SettingsSnapshot) => void;
  onAccessChange: (access: AccessSnapshot) => void;
}

export function TitleManagementSettings({ values, access, onChange }: Pick<TitleSettingsProps, "values" | "access" | "onChange">) {
  const canEdit = access?.can_edit !== false;
  const titleValue = canEdit ? values.title : access?.effective_title ?? values.title;

  return <div className="title-management-stack">
    <div className="title-settings">
      <label className="title-toggle" aria-label="启用头衔"><input type="checkbox" checked={values.title_enabled} onChange={(event) => onChange({ title_enabled: event.target.checked })} /><span className="switch-track" aria-hidden="true" /></label>
      <label><span className="sr-only">头衔</span><input type="text" maxLength={100} aria-invalid={canEdit && values.title_enabled && !values.title.trim()} disabled={!values.title_enabled} readOnly={!canEdit} value={titleValue} onChange={(event) => { if (canEdit) onChange({ title: event.target.value }); }} placeholder="请输入你喜欢的头衔" /></label>
    </div>
    {!canEdit ? <p className="panel-state title-management-note">这是管理员配置的自定义头衔；你只能开启或关闭显示。</p> : null}
  </div>;
}

export function TitleDeletionSettings({ access, deletionDisabled, onDeleted, onAccessChange }: Pick<TitleSettingsProps, "access" | "deletionDisabled" | "onDeleted" | "onAccessChange">) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const canEdit = access?.can_edit !== false;

  async function deleteTitle(): Promise<void> {
    if (!access?.can_delete || deleting) return;
    setDeleting(true);
    setError("");
    try {
      const snapshot = await settingsApi.deleteTitle(crypto.randomUUID());
      onDeleted(snapshot);
      setConfirming(false);
      try {
        onAccessChange(await settingsApi.loadAccess());
      } catch {
        setError("头衔已删除，但删除额度状态暂时无法刷新。");
      }
    } catch {
      setError("删除失败，请稍后重试。");
    } finally {
      setDeleting(false);
    }
  }

  if (!canEdit) return <p className="panel-state">管理员配置的自定义头衔不能由个人删除。</p>;

  return <section className="title-delete-panel" aria-labelledby="title-delete-heading">
      <div><h3 id="title-delete-heading">彻底删除头衔</h3><p>删除当前头衔和配置。按北京时间（Asia/Shanghai）每个自然日只能操作一次。</p></div>
      {access?.deleted_today ? <p className="quota-used">今日删除次数已用尽，下次可用时间：{shanghaiResetLabel(access.reset_at)}</p> : null}
      {!confirming || !access ? <button className="danger" type="button" disabled={deleting || deletionDisabled || !access?.can_delete} onClick={() => setConfirming(true)}>{access ? "彻底删除头衔" : "正在读取删除额度…"}</button> : <div className="title-delete-confirmation" role="alertdialog" aria-labelledby="title-delete-confirm-heading">
        <strong id="title-delete-confirm-heading">确认彻底删除？</strong>
        <p>此操作会立即移除当前头衔，并消耗今日唯一一次删除额度。</p>
        <div><button className="secondary" type="button" disabled={deleting} onClick={() => setConfirming(false)}>取消</button><button className="danger" type="button" disabled={deleting || deletionDisabled || !access.can_delete} onClick={() => void deleteTitle()}>{deleting ? "删除中…" : "确认彻底删除"}</button></div>
      </div>}
      {error ? <p className="action-error" role="alert">{error}</p> : null}
    </section>;
}

/** 兼容独立使用方；Activity 导航会把管理与删除展示为两个常驻面板。 */
export function TitleSettings(props: TitleSettingsProps) {
  return <div className="title-editor-stack">
    <TitleManagementSettings values={props.values} access={props.access} onChange={props.onChange} />
    <TitleDeletionSettings access={props.access} deletionDisabled={props.deletionDisabled} onDeleted={props.onDeleted} onAccessChange={props.onAccessChange} />
  </div>;
}

function roleColor(color: number): string {
  return `#${color.toString(16).padStart(6, "0")}`;
}

export function AdminRolesSettings() {
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyRoleId, setBusyRoleId] = useState<string | null>(null);

  function loadRoles(): void {
    setLoading(true);
    setError("");
    void settingsApi.loadAdminRoles().then(setRoles).catch(() => {
      setRoles([]);
      setError("无法读取 Discord 角色列表。");
    }).finally(() => setLoading(false));
  }

  useEffect(() => { loadRoles(); }, []);

  async function updateRole(role: AdminRole, custom: boolean): Promise<void> {
    if (role.locked || busyRoleId !== null) return;
    setBusyRoleId(role.id);
    setError("");
    try {
      await settingsApi.updateAdminRoleCustom(role.id, custom, crypto.randomUUID());
      setRoles((current) => current.map((item) => item.id === role.id ? { ...item, custom, system: !custom } : item));
    } catch (error) {
      setError(settingsSaveErrorMessage(error));
    } finally {
      setBusyRoleId(null);
    }
  }

  if (loading) return <SkeletonGrid items={6} variant="row" label="正在读取 Discord 角色" />;
  if (error && roles.length === 0) return <div className="panel-error" role="alert"><p>{error}</p><button className="secondary outlined" type="button" onClick={loadRoles}>重试</button></div>;
  const visibleRoles = roles.filter((role) => !role.locked);
  return <div className="admin-role-list">
    {error ? <p className="action-error" role="alert">{error}</p> : null}
    {visibleRoles.length === 0 ? <p className="panel-state">没有可配置的角色。</p> : visibleRoles.map((role) => <article className="admin-role-row" key={role.id}>
      <span className="role-color-dot" style={{ backgroundColor: roleColor(role.color) }} aria-hidden="true" />
      <span className="role-icon-slot" aria-hidden="true">{role.icon_url ? <img src={role.icon_url} alt="" /> : role.unicode_emoji ? <span>{role.unicode_emoji}</span> : null}</span>
      <span className="role-name">{role.name}</span>
      <label className="title-toggle role-system-toggle"><span>自定义头衔</span><input type="checkbox" aria-label={`${role.name}：自定义头衔`} checked={role.custom} disabled={busyRoleId === role.id} onChange={(event) => void updateRole(role, event.target.checked)} /><span className="switch-track" aria-hidden="true" /></label>
    </article>)}
  </div>;
}
